import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Send, CheckCircle2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";

/**
 * AgencyContact — the tenant's Contact page.
 *
 * The form posts a lead into the CRM's public buyer-register endpoint
 * with type=applicant, tagged `source=contact_form`. The agent sees
 * the enquiry in their CRM within seconds. No spam, no third-party
 * form-catcher — everything stays inside Foundation.
 */
export default function AgencyContact() {
  const { slug: urlSlug } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const isUs = market === "us";
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const tenantSlug = urlSlug || brand.tenant_slug || "foundation";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      const nameParts = form.name.trim().split(/\s+/);
      const first = nameParts[0] || form.name.trim();
      const last = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
      await api.post("/crm/public/register-applicant", {
        tenant_slug: tenantSlug,
        first_name: first,
        last_name: last,
        email: form.email.trim().toLowerCase(),
        phone: form.phone || "",
        notes: form.message,
        source: "contact_form",
        marketing_optin: false,
        gdpr_consent: true,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={biz.name ? `Contact ${biz.name} | ${brand.regions_covered_prose || "Estate Agents"}` : undefined}
        description={`Get in touch with ${biz.name || "our team"}${biz.phone ? ` on ${biz.phone}` : ""} about ${isUs ? "selling, renting out, buying or renting" : "selling, letting, buying or renting"} in ${brand.regions_covered_prose || "your area"}.`}
        image={brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : undefined}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="overline">Contact {biz.name || "us"}</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-primary mt-6 tracking-tight leading-[1.05]">
            Let's talk.
          </h1>
          <p className="text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">
            Whether you're {isUs ? "selling, renting out, buying or renting" : "selling, letting, buying or renting"}, pick up the phone or send us a message and we'll come back to you the same day.
          </p>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Contact details */}
            <div className="lg:col-span-2 space-y-6">
              {biz.phone && (
                <ContactDetail icon={Phone} label="Phone" value={biz.phone} href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`} testid="contact-phone" />
              )}
              {biz.email && (
                <ContactDetail icon={Mail} label="Email" value={biz.email} href={`mailto:${biz.email}`} testid="contact-email" />
              )}
              {biz.address && (
                <ContactDetail icon={MapPin} label="Office" value={biz.address} testid="contact-address" />
              )}
              {biz.opening_hours && (
                <div>
                  <div className="overline">Opening hours</div>
                  <p className="text-primary mt-2 whitespace-pre-line text-sm leading-relaxed">{biz.opening_hours}</p>
                </div>
              )}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {sent ? (
                <div className="border border-accent/40 bg-accent/5 rounded-sm p-8 text-center" data-testid="contact-sent">
                  <CheckCircle2 className="w-10 h-10 text-accent mx-auto" />
                  <h2 className="font-serif-display text-3xl text-primary mt-4">Thank you.</h2>
                  <p className="text-muted-foreground mt-4 max-w-md mx-auto leading-relaxed">
                    Your message is with the {biz.name} team. We'll come back to you today.
                  </p>
                  <Link to={tenantPath("")}>
                    <Button variant="outline" className="rounded-sm mt-6">Back to homepage</Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-border bg-card rounded-sm p-6 lg:p-8 space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-name-input">Your name</Label>
                      <Input id="contact-name-input" value={form.name} onChange={set("name")} data-testid="contact-name" required />
                    </div>
                    <div>
                      <Label htmlFor="contact-email-field">Email</Label>
                      <Input id="contact-email-field" type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="contact-phone-field">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input id="contact-phone-field" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-message-field">How can we help?</Label>
                    <Textarea id="contact-message-field" rows={6} value={form.message} onChange={set("message")} data-testid="contact-message" required />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="rounded-sm h-12 px-8 sharp-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? "Sending…" : "Send message"} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    By submitting you agree to our{brand.legal?.privacy ? (
                      <> <Link to={tenantPath("privacy")} className="underline underline-offset-2">privacy policy</Link>.</>
                    ) : " privacy policy."} We'll only use your details to reply to you.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ContactDetail({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <Icon className="w-5 h-5 text-accent shrink-0 mt-0.5" />
      <div>
        <div className="overline">{label}</div>
        <div className="text-2xl font-serif-display text-primary mt-1 group-hover:text-accent transition-colors" data-testid={testid}>
          {value}
        </div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : inner;
}
